/**
 * Burger-Menü (links): Haus, Bearbeiten-Modus, Ebenen, Einrichtungen, Leitungspläne, Möbelliste,
 * Platzieren, Daten, Hilfe, Nutzer.
 */
import { useEffect, useRef, useState } from "react";
import { HOUSES, LINE_TYPES, houseInfo, type LayoutKind, type LayoutSummary } from "@hausplaner/model";
import {
  useStore, setMenu, setEdit, setLayer, setPrefs, lockAll, clearAll, applyData, serialize, toast, touch,
  select, setFloor, toggleLock, furnitureAllowed, kindsFor, KIND_LABEL, type FloorSel,
} from "../state/store";
import { openDoc, createDoc, duplicateDoc, renameDoc, deleteDoc, refreshList, logout } from "../state/layouts";
import { getDevUser, setDevUser } from "../api/client";
import { FLOORS } from "../engine/constants";
import { LINE_INFO } from "../engine/lines";
import { centerPlanOn } from "../scene/rig";
import { navigate, houseUrl } from "./router";

const cmv = (v: number) => Math.round(v * 100);
const fmt = (iso: string) => new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export function Menu() {
  const open = useStore((s) => s.menu);
  const key = useStore((s) => s.houseKey);
  const online = useStore((s) => s.online);
  const furniture = useStore((s) => furnitureAllowed(s));
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setMenu(false); };
    addEventListener("keydown", h); return () => removeEventListener("keydown", h);
  }, [open]);
  if (!open || !key) return null;
  return (
    <>
      <div className="backdrop" onClick={() => setMenu(false)} />
      <aside className="drawer" id="menu" aria-label="Menü">
        <div className="dr-head">
          <div><div className="k">Rosenstraße 30</div><b>{houseInfo(key).name}</b></div>
          <button className="x" onClick={() => setMenu(false)} aria-label="Menü schließen">×</button>
        </div>
        <HouseSection />
        <EditSection />
        <LayersSection />
        {online
          ? kindsFor(key).map((k) => <DocSection key={k} kind={k} />)
          : <Section title="Speichern"><p className="note">Ohne Server wird alles nur in diesem Browser gespeichert.</p></Section>}
        {furniture && <ListSection />}
        {furniture && <PlaceSection />}
        <DataSection />
        <HelpSection />
        {online && <div className="dr-foot"><UserBox /></div>}
      </aside>
    </>
  );
}

function Section({ title, children, open = true, id }: { title: string; children: React.ReactNode; open?: boolean; id?: string }) {
  return (
    <details className="dr-sec" open={open} id={id}>
      <summary>{title}</summary>
      <div className="dr-body">{children}</div>
    </details>
  );
}

function HouseSection() {
  const key = useStore((s) => s.houseKey)!;
  return (
    <Section title="Haus" id="sec-house">
      <div className="seg seg-full" role="group" aria-label="Zustand">
        {HOUSES.map((h) => (
          <button key={h.key} aria-pressed={key === h.key} onClick={() => { setMenu(false); navigate(houseUrl(h.key)); }}>{h.name}</button>
        ))}
      </div>
      <p className="note">{houseInfo(key).description}.{!houseInfo(key).furniture && " Möbel gibt es nur in der Planung."}</p>
      <button className="btn small" onClick={() => { setMenu(false); navigate("/"); }}>Zur Übersicht</button>
    </Section>
  );
}

function EditSection() {
  const edit = useStore((s) => s.edit);
  const furniture = useStore((s) => furnitureAllowed(s));
  return (
    <Section title="Bearbeiten" id="sec-edit">
      <label className="toggle big" htmlFor="t-edit">
        <span><b>Bearbeiten-Modus</b><small>{furniture ? "Deine Möbel und Leitungen ändern" : "Deine Leitungen ändern"}</small></span>
        <input id="t-edit" type="checkbox" checked={edit} onChange={(e) => setEdit(e.target.checked)} />
      </label>
      {!edit && <p className="note">Ohne Bearbeiten-Modus kann nichts verrutschen – ansehen, messen und begehen geht immer.</p>}
      <p className="note">Alles, was du einzeichnest, gehört nur dir – die anderen sehen es nicht. Wände, Fenster und Türen sind für alle gleich.</p>
    </Section>
  );
}

function LayersSection() {
  const layers = useStore((s) => s.layers);
  const lines = useStore((s) => s.lines);
  const points = useStore((s) => s.points);
  const furniture = useStore((s) => furnitureAllowed(s));
  return (
    <Section title="Ebenen" id="sec-layers">
      <label className="toggle" htmlFor="t-roof"><span>Dach</span><input id="t-roof" type="checkbox" checked={layers.roof} onChange={(e) => setLayer("roof", e.target.checked)} /></label>
      <label className="toggle" htmlFor="t-nb"><span>Nachbarhälfte (Nr. 32)</span><input id="t-nb" type="checkbox" checked={layers.neighbour} onChange={(e) => setLayer("neighbour", e.target.checked)} /></label>
      <label className="toggle" htmlFor="t-lbl"><span>Raumbeschriftung</span><input id="t-lbl" type="checkbox" checked={layers.labels} onChange={(e) => setLayer("labels", e.target.checked)} /></label>
      {furniture && <label className="toggle" htmlFor="t-furn"><span>Möbel</span><input id="t-furn" type="checkbox" checked={layers.furniture} onChange={(e) => setLayer("furniture", e.target.checked)} /></label>}
      <h3 className="subh">Leitungen</h3>
      {LINE_TYPES.map((t) => {
        const n = lines.filter((l) => l.t === t).length;
        return (
          <label key={t} className="toggle" htmlFor={"t-" + t}>
            <span className="swl"><i style={{ background: LINE_INFO[t].color }} />{LINE_INFO[t].name}<small className="meta">{n ? ` ${n}` : ""}</small></span>
            <input id={"t-" + t} type="checkbox" checked={layers[t]} onChange={(e) => setLayer(t, e.target.checked)} />
          </label>
        );
      })}
      {!lines.length && !points.length && <p className="note">Noch keine Leitungen eingezeichnet. Im Bearbeiten-Modus unten auf „Leitungen“.</p>}
    </Section>
  );
}

/** Liste der eigenen Einrichtungen bzw. Leitungspläne – unabhängig voneinander wählbar */
function DocSection({ kind }: { kind: LayoutKind }) {
  const list = useStore((s) => s.lists[kind]);
  const cur = useStore((s) => s.docs[kind].id);
  const [newName, setNewName] = useState("");
  useEffect(() => { void refreshList(kind); }, [kind]);
  const L = KIND_LABEL[kind];
  const fresh = kind === "einrichtung" ? "Neue Einrichtung" : "Neuer Leitungsplan";
  return (
    <Section title={`Meine ${L.many}`} id={`sec-${kind}`}>
      {list.map((l) => <DocRow key={l.id} l={l} kind={kind} cur={l.id === cur} />)}
      <form className="lay-row" onSubmit={(e) => { e.preventDefault(); const n = newName.trim() || fresh; setNewName(""); void createDoc(kind, n, false); }}>
        <input id={`new-${kind}`} placeholder={`${fresh}, z. B. Variante B`} maxLength={60} value={newName} onChange={(e) => setNewName(e.target.value)} />
        <button className="btn small primary" type="submit">Leer anlegen</button>
        <button className="btn small" type="button" onClick={() => { const n = newName.trim() || "Variante"; setNewName(""); void createDoc(kind, n, true); }}>Aktuelle kopieren</button>
      </form>
    </Section>
  );
}

function DocRow({ l, kind, cur }: { l: LayoutSummary; kind: LayoutKind; cur: boolean }) {
  const [edit, setEditName] = useState(false);
  const [name, setName] = useState(l.name);
  const [confirmDel, setConfirmDel] = useState(false);
  if (edit) return (
    <form className="lay-row" onSubmit={(e) => { e.preventDefault(); setEditName(false); if (name.trim() && name.trim() !== l.name) void renameDoc(kind, l.id, name.trim()); }}>
      <input autoFocus maxLength={60} value={name} onChange={(e) => setName(e.target.value)} aria-label="Neuer Name" />
      <button className="btn small primary" type="submit">Speichern</button>
      <button className="btn small" type="button" onClick={() => { setEditName(false); setName(l.name); }}>Abbrechen</button>
    </form>
  );
  const info = kind === "einrichtung" ? `${l.itemCount} Möbel` : `${l.lineCount} Leitungen · ${l.pointCount} Anschlüsse`;
  return (
    <div className={"lay-row" + (cur ? " cur" : "")}>
      <button className="lname" onClick={() => void openDoc(kind, l.id)}>
        <span>{l.name}</span>
        <small>{`${info} · ${fmt(l.updatedAt)}`}</small>
      </button>
      <div className="row">
        <button className="btn small" onClick={() => void duplicateDoc(kind, l.id, `${l.name} (Kopie)`)}>Kopie</button>
        <button className="btn small" onClick={() => setEditName(true)}>Umbenennen</button>
        <button className="btn small danger" onClick={() => { if (!confirmDel) setConfirmDel(true); else void deleteDoc(kind, l.id); }}>{confirmDel ? "Wirklich?" : "Löschen"}</button>
      </div>
    </div>
  );
}

function ListSection() {
  const items = useStore((s) => s.items);
  const bad = useStore((s) => s.bad);
  const coll = useStore((s) => s.prefs.coll);
  const edit = useStore((s) => s.edit);
  const nl = items.filter((i) => i.lock).length, nb = coll ? items.filter((i) => bad.has(i.id)).length : 0;
  return (
    <Section title={`Möbelliste · ${items.length}`} open={false} id="sec-list">
      {!items.length && <p className="note">Noch keine Möbel platziert.</p>}
      {!!items.length && <p className="meta">{`${items.length} Möbel · ${nl} fixiert${nb ? ` · ${nb} mit Überschneidung` : ""}`}</p>}
      {FLOORS.map((F, fi) => {
        const list = items.filter((i) => i.f === fi).sort((a, b) => a.name.localeCompare(b.name, "de"));
        if (!list.length) return null;
        return (
          <div className="cat" key={fi}>
            <h3>{`${F.name} · ${list.length}`}</h3>
            {list.map((it) => {
              const isBad = coll && bad.has(it.id);
              return (
                <div className={"lrow" + (isBad ? " bad" : "")} key={it.id}>
                  <button className="lname" onClick={() => {
                    const s = useStore.getState();
                    setMenu(false);
                    if (s.mode !== "walk" && s.floor !== it.f) setFloor(it.f as FloorSel);
                    if (s.mode === "plan") centerPlanOn(it.x, it.y);
                    select(it.id);
                  }}>
                    <span>{it.name}</span>
                    <small>{`${cmv(it.w)} × ${cmv(it.d)} × ${cmv(it.h)} cm${isBad ? " · Überschneidung" : ""}`}</small>
                  </button>
                  {edit && <button className="btn small" aria-pressed={it.lock} onClick={() => toggleLock(it.id)}>{it.lock ? "Fixiert" : "Fixieren"}</button>}
                </div>
              );
            })}
          </div>
        );
      })}
    </Section>
  );
}

function PlaceSection() {
  const prefs = useStore((s) => s.prefs);
  const edit = useStore((s) => s.edit);
  if (!edit) return null;
  return (
    <Section title="Möbel platzieren" open={false} id="sec-place">
      <label className="toggle" htmlFor="t-mag"><span>Magnet: an Wände und Möbel andocken</span><input id="t-mag" type="checkbox" checked={prefs.magnet} onChange={(e) => setPrefs({ magnet: e.target.checked })} /></label>
      <label className="toggle" htmlFor="t-guide"><span>Abstände zu Wänden anzeigen</span><input id="t-guide" type="checkbox" checked={prefs.guides} onChange={(e) => setPrefs({ guides: e.target.checked })} /></label>
      <label className="toggle" htmlFor="t-coll"><span>Überschneidungen rot markieren</span><input id="t-coll" type="checkbox" checked={prefs.coll} onChange={(e) => setPrefs({ coll: e.target.checked })} /></label>
      <label className="toggle" htmlFor="t-grid"><span>Raster beim Ziehen</span>
        <select id="t-grid" value={String(prefs.grid)} onChange={(e) => setPrefs({ grid: +e.target.value })}>
          <option value="0">aus</option><option value="0.01">1 cm</option><option value="0.05">5 cm</option><option value="0.1">10 cm</option>
        </select>
      </label>
      <div className="row"><button className="btn small" id="x-lockall" onClick={() => lockAll(true)}>Alle fixieren</button><button className="btn small" id="x-unlockall" onClick={() => lockAll(false)}>Alle lösen</button></div>
    </Section>
  );
}

function DataSection() {
  const furniture = useStore((s) => furnitureAllowed(s));
  const edit = useStore((s) => s.edit);
  const key = useStore((s) => s.houseKey)!;
  const docName = useStore((s) => s.docs[furnitureAllowed(s) ? "einrichtung" : "leitungen"].name);
  const [confirm, setConfirm] = useState(false);
  const file = useRef<HTMLInputElement>(null);

  const exportJson = () => {
    const payload = { ...serialize(), houseKey: key, ts: Date.now() };
    const blob = new Blob([JSON.stringify(payload, null, 1)], { type: "application/json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    const base = `${key}-${docName || "export"}`;
    a.download = `rosenstrasse-30-${base.toLowerCase().replace(/[^a-z0-9äöüß]+/g, "-")}.json`;
    document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };
  const importJson = (f: File) => {
    if (!useStore.getState().edit) return toast("Zum Importieren den Bearbeiten-Modus einschalten");
    const r = new FileReader();
    r.onload = () => {
      try {
        const obj = JSON.parse(String(r.result)) as Record<string, unknown> & { house?: Record<string, unknown> };
        // ältere Exporte hatten Leitungen unter "house"
        const merged = { items: [], ...obj, ...(obj.house && typeof obj.house === "object" ? obj.house : {}) };
        applyData(merged, { keepCustom: true });
        touch(); toast("Importiert");
      } catch { toast("Datei konnte nicht gelesen werden. Bitte eine exportierte .json-Datei wählen."); }
    };
    r.readAsText(f);
  };

  return (
    <Section title="Daten" open={false} id="sec-data">
      <div className="row">
        <button className="btn small" id="x-exp" onClick={exportJson}>Exportieren</button>
        <label className={"btn small" + (edit ? "" : " off")} htmlFor="x-imp">Importieren<input id="x-imp" ref={file} type="file" accept="application/json,.json" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) importJson(f); e.target.value = ""; }} /></label>
        {furniture && edit && <button className="btn small danger" id="x-clear" onClick={() => setConfirm(true)}>Alle Möbel entfernen</button>}
      </div>
      <p className="note">Export enthält {furniture ? "die geöffnete Einrichtung und " : ""}den geöffneten Leitungsplan. Import ersetzt deren Inhalt (rückgängig machbar).</p>
      <div className="row" id="clear-confirm" hidden={!confirm}>
        <span className="note">Wirklich alle Möbel entfernen?</span>
        <button className="btn small danger" id="x-clear-yes" onClick={() => { clearAll(); setConfirm(false); }}>Ja, entfernen</button>
        <button className="btn small" id="x-clear-no" onClick={() => setConfirm(false)}>Abbrechen</button>
      </div>
    </Section>
  );
}

function HelpSection() {
  return (
    <Section title="Hilfe & Grundlagen" open={false} id="sec-help">
      <p className="note"><b>Tastatur (Bearbeiten):</b> Pfeiltasten verschieben 1 cm (mit Umschalt 10 cm) · R dreht 90° (Umschalt+R 15°) · W stellt an die Wand · L fixiert · Entf löscht · Strg+D dupliziert · Strg+Z / Strg+Y rückgängig / wiederholen · Enter beendet eine Leitung, Esc bricht ab.</p>
      <p className="note"><b>Grundlage:</b> Bestandspläne BA.01–BA.04 (M 1:100) und Ansicht Nord. Geschosshöhen aus der Ansicht abgeleitet: UG ±0,00 · EG +2,60 · OG +5,35 · DG +8,10, Dachneigung ca. 30°, First ca. +11,50.</p>
      <p className="note"><b>Annahmen:</b> Fensterbrüstungen ca. 85 cm, Türhöhe 2,01 m, Deckenstärke 20 cm.</p>
    </Section>
  );
}

/** angemeldeter Nutzer, Abmelden bzw. Nutzer-Umschalter im Entwicklungsmodus */
export function UserBox({ compact = false }: { compact?: boolean }) {
  const me = useStore((s) => s.me);
  const [dev, setDev] = useState(getDevUser());
  if (!me) return null;
  return (
    <div className={"userbox" + (compact ? " compact" : "")}>
      <span>Angemeldet als <b>{me.displayName}</b></span>
      {me.authMode === "dev" ? (
        <form className="row" onSubmit={(e) => { e.preventDefault(); const v = dev.trim().toLowerCase(); if (v) { setDevUser(v); location.reload(); } }}>
          <label htmlFor={compact ? "dev-user-start" : "dev-user"} className="meta">Dev-Nutzer:</label>
          <input id={compact ? "dev-user-start" : "dev-user"} value={dev} onChange={(e) => setDev(e.target.value)} maxLength={40} />
          <button className="btn small" type="submit">Wechseln</button>
        </form>
      ) : <button className="btn small" id="b-logout" onClick={() => void logout()}>Abmelden</button>}
    </div>
  );
}
